import React, { useState, useEffect, useMemo } from 'react';
import { formatImageUrl } from '../utils/imageUrl';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const PartnersSection = () => {
  const [partners, setPartners] = useState([]);
  const [showSection, setShowSection] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        // 1. Fetch settings for visibility toggle
        const settingsRes = await fetch(`${API_BASE_URL}/settings/public`).catch(() => null);
        if (settingsRes && settingsRes.ok) {
          const settingsData = await settingsRes.json();
          if (settingsData?.show_partners_section === 'false' || settingsData?.show_partners_section === false) {
            if (isMounted) setShowSection(false);
          }
        }

        // 2. Fetch active partners
        const response = await fetch(`${API_BASE_URL}/partners`);
        if (!response.ok) {
          throw new Error('Failed to fetch partners');
        }
        const data = await response.json();
        if (isMounted) {
          setPartners(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('Error fetching DATX partners:', err);
        if (isMounted) {
          setError(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  // If partners count is small (1-4 partners), render a centered static elegant grid instead of marquee
  const isSmallList = partners.length < 5;

  // For marquee: only prepare list if we have 5+ partners
  const basePartners = useMemo(() => {
    if (!partners || partners.length === 0) return [];
    if (isSmallList) return partners;
    return partners;
  }, [partners, isSmallList]);

  // Do not render section if disabled by admin, loading, error, or no active partners found
  if (!showSection || loading || error || !partners || partners.length === 0) {
    return null;
  }

  const renderPartnerItem = (partner, key) => {
    const logoContent = (
      <img
        src={formatImageUrl(partner.logo, '')}
        alt={partner.name || 'شريك النجاح'}
        width="165"
        height="56"
        loading="lazy"
        decoding="async"
        className="h-10 sm:h-12 md:h-14 max-h-[56px] w-auto max-w-[180px] object-contain transition-all duration-300 opacity-85 hover:opacity-100 hover:scale-105 pointer-events-auto mix-blend-multiply"
        onError={(e) => {
          e.currentTarget.style.display = 'none';
          const parent = e.currentTarget.parentElement;
          if (parent && !parent.querySelector('.partner-fallback-badge')) {
            const badge = document.createElement('span');
            badge.className =
              'partner-fallback-badge text-sm sm:text-base font-bold text-[#172033] text-center px-4 py-2 opacity-70 hover:opacity-100 transition-opacity bg-gray-50 rounded-lg border border-gray-200';
            badge.innerText = partner.name || 'شريك معتمد';
            parent.appendChild(badge);
          }
        }}
      />
    );

    if (partner.url) {
      return (
        <a
          key={key}
          href={partner.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`زيارة موقع ${partner.name}`}
          className="flex items-center justify-center shrink-0 p-2 transition-transform duration-300 hover:scale-105"
        >
          {logoContent}
        </a>
      );
    }

    return (
      <div key={key} className="flex items-center justify-center shrink-0 p-2 transition-transform duration-300 hover:scale-105">
        {logoContent}
      </div>
    );
  };

  return (
    <section
      className="py-12 sm:py-16 bg-white border-t border-b border-gray-100 relative overflow-hidden text-right font-arabic w-full"
      dir="rtl"
      aria-label="شركاء النجاح"
    >
      {/* Section Header */}
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center mb-8 sm:mb-10">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-[#172033] mb-2 tracking-tight">
          شركاء <span className="text-[#173AA8]">النجاح</span>
        </h2>
        <div className="w-12 h-1 bg-[#173AA8] rounded-full mx-auto mb-3" />
        <p className="text-sm sm:text-base text-[#64748B] max-w-xl mx-auto font-normal leading-relaxed">
          نفخر بالتعاون مع جهات وشركات تسهم في تطوير المعرفة والمهارات.
        </p>
      </div>

      {/* When partners are 1 to 4: Centered Elegant Static Display */}
      {isSmallList ? (
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-center gap-8 sm:gap-14 md:gap-20 py-2">
          {partners.map((partner, index) => renderPartnerItem(partner, `partner-static-${partner.id || index}`))}
        </div>
      ) : (
        /* Infinite Seamless Conveyor (Only when 5+ partners) */
        <div className="relative w-full overflow-hidden py-4 datx-marquee-wrapper select-none" dir="ltr">
          {/* Soft edge gradient fades */}
          <div
            className="absolute top-0 right-0 bottom-0 w-20 sm:w-36 bg-gradient-to-l from-white to-transparent z-20 pointer-events-none"
            aria-hidden="true"
          />
          <div
            className="absolute top-0 left-0 bottom-0 w-20 sm:w-36 bg-gradient-to-r from-white to-transparent z-20 pointer-events-none"
            aria-hidden="true"
          />

          {/* 2-Track Continuous Unbroken Row */}
          <div
            className="flex flex-row w-max animate-marquee-continuous"
            style={{
              animationDuration: '16s',
              animationTimingFunction: 'linear',
              animationIterationCount: 'infinite',
              willChange: 'transform',
            }}
          >
            {/* Primary Track */}
            <div className="flex flex-row items-center gap-12 sm:gap-16 md:gap-20 shrink-0 pr-12 sm:pr-16 md:pr-20">
              {basePartners.map((partner, index) => renderPartnerItem(partner, `track1-${partner.id || index}-${index}`))}
            </div>

            {/* Secondary Clone Track for seamless loop */}
            <div className="flex flex-row items-center gap-12 sm:gap-16 md:gap-20 shrink-0 pr-12 sm:pr-16 md:pr-20" aria-hidden="true">
              {basePartners.map((partner, index) => renderPartnerItem(partner, `track2-${partner.id || index}-${index}`))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PartnersSection;
